import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { reminders as remindersApi, type Reminder } from '../lib/api';

const OFFSETS = [
  { value: 'day_before', label: '1 day before due date' },
  { value: 'week_before', label: '1 week before due date' },
  { value: 'month_before', label: '1 month before due date' },
];

function computePreviewDate(dueDate: string, offset: string): string {
  if (!dueDate) return '';
  const d = new Date(dueDate);
  if (isNaN(d.getTime())) return '';
  if (offset === 'day_before') d.setDate(d.getDate() - 1);
  else if (offset === 'week_before') d.setDate(d.getDate() - 7);
  else if (offset === 'month_before') d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
}

export default function ReminderForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [offset, setOffset] = useState('week_before');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    remindersApi.list().then(({ reminders }) => {
      const r = reminders.find((x: Reminder) => x.id === id);
      if (r) {
        setTitle(r.title);
        setDescription(r.description || '');
        setDueDate(r.due_date);
        setOffset(r.reminder_offset);
      }
    }).finally(() => setLoadingData(false));
  }, [id]);

  const reminderPreview = computePreviewDate(dueDate, offset);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await remindersApi.update(id!, { title, description, due_date: dueDate, reminder_offset: offset as any });
      } else {
        await remindersApi.create({ title, description, due_date: dueDate, reminder_offset: offset });
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to save reminder');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Reminder' : 'New Reminder'}
        </h1>
      </div>

      <div className="card space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="label">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. Dad's birthday, Rent payment"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              maxLength={120}
            />
          </div>

          <div>
            <label className="label">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input-field resize-none"
              placeholder="Add any details or notes..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div>
            <label className="label">Due date <span className="text-red-500">*</span></label>
            <input
              type="date"
              className="input-field"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              min={today}
              required
            />
          </div>

          <div>
            <label className="label">When to remind me <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {OFFSETS.map(o => (
                <label key={o.value} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
                  <input
                    type="radio"
                    name="offset"
                    value={o.value}
                    checked={offset === o.value}
                    onChange={() => setOffset(o.value)}
                    className="text-blue-600 w-4 h-4"
                  />
                  <span className="font-medium text-gray-700">{o.label}</span>
                </label>
              ))}
            </div>
          </div>

          {reminderPreview && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              You&apos;ll be reminded on <strong>{reminderPreview}</strong>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Link to="/dashboard" className="flex-1 btn-secondary text-center">Cancel</Link>
            <button type="submit" className="flex-1 btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
